import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { StatusPill, RiskPill } from "./MerchantOverview";
import { Sparkles, Users, Building2, Receipt, AlertTriangle, FileSignature, BarChart3, ShieldCheck, Wallet, Settings } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { PlansAdminTab, BillingAdminTab, SystemAdminTab } from "./AdminTabs";

export default function AdminDashboard() {
  const { user, formatApiError } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [tx, setTx] = useState([]);
  const [users, setUsers] = useState([]);
  const [chargebacks, setChargebacks] = useState([]);
  const [creditApps, setCreditApps] = useState([]);

  const load = () => Promise.all([
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}),
    api.get("/admin/merchants").then((r) => setMerchants(r.data)).catch(() => {}),
    api.get("/transactions").then((r) => setTx(r.data)).catch(() => {}),
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {}),
    api.get("/chargebacks").then((r) => setChargebacks(r.data)).catch(() => {}),
    api.get("/admin/credit-applications").then((r) => setCreditApps(r.data)).catch(() => {}),
  ]);
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="admin-dashboard">
      <p className="text-xs uppercase tracking-[0.18em] text-[#3B82F6]">Admin</p>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-2">Underwriter Console</h1>
      <p className="text-slate-500 mt-2">Approve merchants, manage chargebacks, run AI risk analysis, and oversee users.</p>

      <Tabs value={tab} onValueChange={setTab} className="mt-8">
        <TabsList className="bg-[#FFFFFF] border border-slate-200 flex-wrap h-auto">
          <TabsTrigger value="overview" className="data-[state=active]:bg-slate-100" data-testid="admin-tab-overview"><BarChart3 className="w-4 h-4 mr-2" /> Overview</TabsTrigger>
          <TabsTrigger value="merchants" className="data-[state=active]:bg-slate-100" data-testid="admin-tab-merchants"><Building2 className="w-4 h-4 mr-2" /> Merchants</TabsTrigger>
          <TabsTrigger value="transactions" className="data-[state=active]:bg-slate-100" data-testid="admin-tab-transactions"><Receipt className="w-4 h-4 mr-2" /> Transactions</TabsTrigger>
          <TabsTrigger value="chargebacks" className="data-[state=active]:bg-slate-100" data-testid="admin-tab-chargebacks"><AlertTriangle className="w-4 h-4 mr-2" /> Chargebacks</TabsTrigger>
          <TabsTrigger value="credit" className="data-[state=active]:bg-slate-100" data-testid="admin-tab-credit"><FileSignature className="w-4 h-4 mr-2" /> Credit</TabsTrigger>
          <TabsTrigger value="users" className="data-[state=active]:bg-slate-100" data-testid="admin-tab-users"><Users className="w-4 h-4 mr-2" /> Users</TabsTrigger>
          <TabsTrigger value="plans" className="data-[state=active]:bg-slate-100" data-testid="admin-tab-plans"><FileSignature className="w-4 h-4 mr-2" /> Plans</TabsTrigger>
          <TabsTrigger value="billing-admin" className="data-[state=active]:bg-slate-100" data-testid="admin-tab-billing"><Wallet className="w-4 h-4 mr-2" /> Billing</TabsTrigger>
          <TabsTrigger value="system" className="data-[state=active]:bg-slate-100" data-testid="admin-tab-system"><Settings className="w-4 h-4 mr-2" /> System</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <OverviewTab stats={stats} />
        </TabsContent>
        <TabsContent value="merchants" className="mt-6">
          <MerchantsTab rows={merchants} reload={load} />
        </TabsContent>
        <TabsContent value="transactions" className="mt-6">
          <TransactionsTab rows={tx} reload={load} />
        </TabsContent>
        <TabsContent value="chargebacks" className="mt-6">
          <ChargebacksTab rows={chargebacks} reload={load} />
        </TabsContent>
        <TabsContent value="credit" className="mt-6">
          <CreditTab rows={creditApps} reload={load} fmtErr={formatApiError} />
        </TabsContent>
        <TabsContent value="users" className="mt-6">
          <UsersTab rows={users} reload={load} me={user} />
        </TabsContent>
        <TabsContent value="plans" className="mt-6">
          <PlansAdminTab />
        </TabsContent>
        <TabsContent value="billing-admin" className="mt-6">
          <BillingAdminTab users={users} />
        </TabsContent>
        <TabsContent value="system" className="mt-6">
          <SystemAdminTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatBox({ label, value, accent = "#FFFFFF" }) {
  return (
    <div className="sk-card p-5">
      <div className="text-xs uppercase tracking-wider text-slate-400">{label}</div>
      <div className="mt-2 font-heading text-2xl" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function OverviewTab({ stats }) {
  return (
    <div className="space-y-4" data-testid="admin-overview">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatBox label="Pending Merchants" value={stats?.pending_merchants ?? 0} accent="#F59E0B" />
        <StatBox label="Approved Merchants" value={stats?.approved_merchants ?? 0} accent="#10B981" />
        <StatBox label="High-Risk Tx" value={stats?.high_risk_transactions ?? 0} accent="#FF4757" />
        <StatBox label="Open Chargebacks" value={stats?.open_chargebacks ?? 0} accent="#3B82F6" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatBox label="Total Volume" value={`$${(stats?.total_volume || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} accent="#00D533" />
        <StatBox label="Total Tx" value={stats?.total_transactions ?? 0} />
        <StatBox label="Total Merchants" value={stats?.total_merchants ?? 0} />
        <StatBox label="Total Chargebacks" value={stats?.total_chargebacks ?? 0} />
      </div>
    </div>
  );
}

function MerchantsTab({ rows, reload }) {
  const decide = async (id, action) => {
    try {
      await api.post(`/admin/merchants/${id}/${action}`);
      toast.success(`Merchant ${action}d`);
      reload();
    } catch { toast.error("Action failed"); }
  };
  return (
    <div className="sk-card overflow-hidden" data-testid="admin-merchants-table">
      <div className="grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
        <div className="col-span-3">Business</div>
        <div className="col-span-2">Industry</div>
        <div className="col-span-2">Vol / Ticket</div>
        <div className="col-span-2">Status</div>
        <div className="col-span-3 text-right">Actions</div>
      </div>
      {rows.length === 0 && <div className="py-12 text-center text-slate-400 text-sm">No merchants.</div>}
      {rows.map((m) => (
        <div key={m.merchant_id} className="grid grid-cols-12 items-center px-4 py-3 text-sm border-b border-slate-100 hover:bg-slate-50">
          <div className="col-span-3">
            <div className="text-slate-800 truncate">{m.legal_business_name}</div>
            <div className="font-mono text-xs text-slate-400">{m.merchant_id}</div>
          </div>
          <div className="col-span-2 text-slate-500">{m.industry}</div>
          <div className="col-span-2 text-slate-500 font-mono text-xs">${Number(m.monthly_volume).toLocaleString()} / ${Number(m.avg_ticket).toLocaleString()}</div>
          <div className="col-span-2"><StatusPill status={m.status} /></div>
          <div className="col-span-3 flex justify-end gap-2">
            {m.status === "pending" ? (
              <>
                <Button size="sm" className="bg-[#10B981] text-black hover:bg-[#10C99A]" onClick={() => decide(m.merchant_id, "approve")} data-testid={`approve-${m.merchant_id}`}>Approve</Button>
                <Button size="sm" variant="ghost" className="border border-[#FF4757]/40 text-[#FF4757] hover:bg-[#FF4757]/10" onClick={() => decide(m.merchant_id, "reject")} data-testid={`reject-${m.merchant_id}`}>Reject</Button>
              </>
            ) : (
              <span className="text-xs text-slate-400">{m.risk_tier ? `Tier: ${m.risk_tier}` : "Decided"}</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function TransactionsTab({ rows, reload }) {
  const [openTx, setOpenTx] = useState(null);
  const [risk, setRisk] = useState(null);
  const [loadingAi, setLoadingAi] = useState(false);
  const [override, setOverride] = useState(null);
  const [oStatus, setOStatus] = useState("captured");
  const [oNote, setONote] = useState("");

  const runRisk = async (t) => {
    setOpenTx(t); setRisk(null); setLoadingAi(true);
    try {
      const { data } = await api.post(`/transactions/${t.transaction_id}/risk-analysis`);
      setRisk(data);
    } catch { toast.error("AI risk failed"); }
    finally { setLoadingAi(false); }
  };

  const submitOverride = async () => {
    try {
      await api.post(`/admin/transactions/${override.transaction_id}/status`, { status: oStatus, note: oNote });
      toast.success("Status overridden");
      setOverride(null); setONote("");
      reload();
    } catch { toast.error("Override failed"); }
  };

  return (
    <div data-testid="admin-tx-table">
      <div className="sk-card overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
          <div className="col-span-3">Transaction</div>
          <div className="col-span-2">Type</div>
          <div className="col-span-1">Risk</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2 text-right">Amount</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {rows.length === 0 && <div className="py-12 text-center text-slate-400 text-sm">No transactions.</div>}
        {rows.slice(0, 50).map((t) => (
          <div key={t.transaction_id} className="grid grid-cols-12 items-center px-4 py-3 text-sm border-b border-slate-100 hover:bg-slate-50">
            <div className="col-span-3 font-mono text-xs text-slate-500 truncate">{t.transaction_id}</div>
            <div className="col-span-2 capitalize text-slate-600">{t.type}</div>
            <div className="col-span-1"><RiskPill tier={t.risk_tier} /></div>
            <div className="col-span-2"><StatusPill status={t.status} /></div>
            <div className="col-span-2 text-right font-mono">${Number(t.amount).toFixed(2)}</div>
            <div className="col-span-2 flex justify-end gap-2">
              <Button size="sm" variant="ghost" className="text-xs border border-slate-200 hover:bg-slate-100" onClick={() => runRisk(t)} data-testid={`admin-ai-${t.transaction_id}`}>
                <Sparkles className="w-3.5 h-3.5 mr-1" /> AI
              </Button>
              <Button size="sm" variant="ghost" className="text-xs border border-slate-200 hover:bg-slate-100" onClick={() => setOverride(t)} data-testid={`admin-override-${t.transaction_id}`}>
                Override
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!openTx} onOpenChange={(v) => !v && setOpenTx(null)}>
        <DialogContent className="bg-[#FFFFFF] border-slate-200 text-slate-900 max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading text-2xl">AI Risk Analysis</DialogTitle></DialogHeader>
          {loadingAi && <div className="py-8 text-center text-slate-500">Running Claude analysis...</div>}
          {risk && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <StatBox label="Rule" value={`${risk.rule_score}`} />
                <StatBox label="AI" value={`${risk.ai_score}`} accent="#00D533" />
                <StatBox label="Tier" value={(risk.tier || "").toUpperCase()} accent={risk.tier === "high" ? "#FF4757" : risk.tier === "medium" ? "#F59E0B" : "#10B981"} />
              </div>
              <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                <div className="text-xs uppercase tracking-wider text-slate-400">Recommendation</div>
                <div className="mt-1 capitalize">{risk.recommended_action?.replace("_", " ")}</div>
                {risk.summary && <p className="mt-2 text-sm text-slate-600">{risk.summary}</p>}
              </div>
              {risk.aml_flags?.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#FF4757] mb-2">AML Flags</div>
                  <div className="flex flex-wrap gap-2">
                    {risk.aml_flags.map((f, i) => (
                      <span key={i} className="px-2 py-0.5 rounded bg-[#FF4757]/15 text-[#FF4757] text-xs border border-[#FF4757]/30">{f}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!override} onOpenChange={(v) => !v && setOverride(null)}>
        <DialogContent className="bg-[#FFFFFF] border-slate-200 text-slate-900 max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-xl">Override Status</DialogTitle></DialogHeader>
          {override && (
            <div className="space-y-4">
              <div className="text-sm text-slate-500 font-mono">{override.transaction_id}</div>
              <div>
                <label className="text-sm text-slate-600">New status</label>
                <Select value={oStatus} onValueChange={setOStatus}>
                  <SelectTrigger className="mt-2 bg-[#F8FAFC] border-slate-200 h-11" data-testid="override-status-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#FFFFFF] border-slate-200 text-slate-900">
                    <SelectItem value="captured">Captured</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="pending_review">Pending Review</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                    <SelectItem value="refunded">Refunded</SelectItem>
                    <SelectItem value="voided">Voided</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-slate-600">Note</label>
                <Textarea value={oNote} onChange={(e) => setONote(e.target.value)}
                  className="mt-2 bg-[#F8FAFC] border-slate-200 min-h-[80px]" data-testid="override-note-input" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={submitOverride} className="bg-[#00D533] hover:bg-[#00E636] text-black font-semibold" data-testid="override-submit-btn">Apply Override</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ChargebacksTab({ rows, reload }) {
  const [active, setActive] = useState(null);
  const [decision, setDecision] = useState("won");
  const [note, setNote] = useState("");

  const submit = async () => {
    try {
      await api.post(`/admin/chargebacks/${active.chargeback_id}/decide`, { decision, note });
      toast.success(`Chargeback marked ${decision}`);
      setActive(null); setNote("");
      reload();
    } catch { toast.error("Action failed"); }
  };

  return (
    <div data-testid="admin-cb-table">
      <div className="sk-card overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
          <div className="col-span-3">ID</div>
          <div className="col-span-3">Reason</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2">Guarantee</div>
          <div className="col-span-2 text-right">Disputed</div>
        </div>
        {rows.length === 0 && <div className="py-12 text-center text-slate-400 text-sm">No chargebacks.</div>}
        {rows.map((cb) => (
          <button key={cb.chargeback_id} onClick={() => { setActive(cb); setDecision("won"); }}
            className="w-full text-left grid grid-cols-12 items-center px-4 py-3 text-sm border-b border-slate-100 hover:bg-slate-50"
            data-testid={`admin-cb-row-${cb.chargeback_id}`}>
            <div className="col-span-3 font-mono text-xs text-slate-500 truncate">{cb.chargeback_id}</div>
            <div className="col-span-3 truncate">{cb.reason}</div>
            <div className="col-span-2"><StatusPill status={cb.status} /></div>
            <div className="col-span-2">
              {cb.guarantee_eligible ? (
                <span className="inline-flex items-center gap-1 text-xs text-[#10B981]"><ShieldCheck className="w-3 h-3" /> Eligible</span>
              ) : <span className="text-xs text-slate-400">Not eligible</span>}
            </div>
            <div className="col-span-2 text-right font-mono">${Number(cb.amount_disputed).toFixed(2)}</div>
          </button>
        ))}
      </div>

      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className="bg-[#FFFFFF] border-slate-200 text-slate-900 max-w-lg">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Decide Chargeback</DialogTitle></DialogHeader>
          {active && (
            <div className="space-y-4">
              <div className="text-sm text-slate-500 font-mono">{active.chargeback_id}</div>
              <div className="text-sm">Reason: {active.reason}</div>
              <div>
                <label className="text-sm text-slate-600">Decision</label>
                <Select value={decision} onValueChange={setDecision}>
                  <SelectTrigger className="mt-2 bg-[#F8FAFC] border-slate-200 h-11" data-testid="cb-decision-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#FFFFFF] border-slate-200 text-slate-900">
                    <SelectItem value="won">Won (merchant favor)</SelectItem>
                    <SelectItem value="lost">Lost (customer favor)</SelectItem>
                    <SelectItem value="guarantee_paid">Guarantee Paid (SelleCheck covers)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-slate-600">Note</label>
                <Textarea value={note} onChange={(e) => setNote(e.target.value)}
                  className="mt-2 bg-[#F8FAFC] border-slate-200 min-h-[80px]" data-testid="cb-decision-note" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={submit} className="bg-[#00D533] hover:bg-[#00E636] text-black font-semibold" data-testid="cb-decision-submit">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CreditTab({ rows, reload, fmtErr }) {
  const [active, setActive] = useState(null);
  const [decision, setDecision] = useState("approved");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");

  const submit = async () => {
    try {
      const payload = { decision, note };
      if (amount) payload.approved_amount = parseFloat(amount);
      await api.post(`/admin/credit-applications/${active.credit_app_id}/decide`, payload);
      toast.success(`Application ${decision}`);
      setActive(null); setAmount(""); setNote("");
      reload();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail) || "Failed"); }
  };

  return (
    <div data-testid="admin-credit-table">
      <div className="sk-card overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
          <div className="col-span-3">ID</div>
          <div className="col-span-2">Score</div>
          <div className="col-span-2">Requested</div>
          <div className="col-span-2">Approved</div>
          <div className="col-span-2">Decision</div>
          <div className="col-span-1 text-right"></div>
        </div>
        {rows.length === 0 && <div className="py-12 text-center text-slate-400 text-sm">No credit apps.</div>}
        {rows.map((c) => (
          <div key={c.credit_app_id} className="grid grid-cols-12 items-center px-4 py-3 text-sm border-b border-slate-100 hover:bg-slate-50">
            <div className="col-span-3 font-mono text-xs text-slate-500 truncate">{c.credit_app_id}</div>
            <div className="col-span-2 font-mono">{c.composite_score}</div>
            <div className="col-span-2 font-mono">${Number(c.requested_amount).toLocaleString()}</div>
            <div className="col-span-2 font-mono">${Number(c.approved_amount || 0).toLocaleString()}</div>
            <div className="col-span-2">
              <span className="px-2 py-0.5 rounded text-xs uppercase tracking-wider"
                style={{
                  background: c.decision === "approved" ? "#10B98119" : c.decision === "declined" ? "#FF475719" : "#F59E0B19",
                  color: c.decision === "approved" ? "#10B981" : c.decision === "declined" ? "#FF4757" : "#F59E0B",
                }}>{c.decision}</span>
            </div>
            <div className="col-span-1 text-right">
              <Button size="sm" variant="ghost" className="text-xs border border-slate-200 hover:bg-slate-100" onClick={() => { setActive(c); setDecision(c.decision); setAmount(""); }} data-testid={`credit-decide-${c.credit_app_id}`}>Decide</Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className="bg-[#FFFFFF] border-slate-200 text-slate-900 max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-xl">Override Credit Decision</DialogTitle></DialogHeader>
          {active && (
            <div className="space-y-4">
              <div className="text-sm text-slate-500 font-mono">{active.credit_app_id}</div>
              <div>
                <label className="text-sm text-slate-600">Decision</label>
                <Select value={decision} onValueChange={setDecision}>
                  <SelectTrigger className="mt-2 bg-[#F8FAFC] border-slate-200 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#FFFFFF] border-slate-200 text-slate-900">
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="manual_review">Manual review</SelectItem>
                    <SelectItem value="declined">Declined</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-slate-600">Approved amount (USD)</label>
                <Input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={`${active.approved_amount || active.requested_amount}`} className="mt-2 bg-[#F8FAFC] border-slate-200 h-11" data-testid="credit-amount-input" />
              </div>
              <div>
                <label className="text-sm text-slate-600">Admin note</label>
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} className="mt-2 bg-[#F8FAFC] border-slate-200 min-h-[80px]" data-testid="credit-note-input" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={submit} className="bg-[#00D533] hover:bg-[#00E636] text-black font-semibold" data-testid="credit-decide-submit">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function UsersTab({ rows, reload, me }) {
  const setRole = async (u, role) => {
    try {
      await api.post(`/admin/users/${u.user_id}/role`, { role });
      toast.success(`Role updated to ${role}`);
      reload();
    } catch { toast.error("Could not update role"); }
  };

  const toggleActive = async (u) => {
    try {
      const path = u.deactivated ? "reactivate" : "deactivate";
      await api.post(`/admin/users/${u.user_id}/${path}`);
      toast.success(u.deactivated ? "User reactivated" : "User deactivated");
      reload();
    } catch { toast.error("Action failed"); }
  };

  return (
    <div className="sk-card overflow-hidden" data-testid="admin-users-table">
      <div className="grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
        <div className="col-span-3">Email</div>
        <div className="col-span-3">Name</div>
        <div className="col-span-2">Role</div>
        <div className="col-span-2">Provider</div>
        <div className="col-span-2 text-right">Actions</div>
      </div>
      {rows.length === 0 && <div className="py-12 text-center text-slate-400 text-sm">No users.</div>}
      {rows.map((u) => {
        const isMe = u.user_id === me?.user_id;
        return (
          <div key={u.user_id} className="grid grid-cols-12 items-center px-4 py-3 text-sm border-b border-slate-100 hover:bg-slate-50">
            <div className="col-span-3">
              <div className="text-slate-800 truncate">{u.email}</div>
              {u.deactivated && <span className="text-xs text-[#FF4757]">deactivated</span>}
            </div>
            <div className="col-span-3 text-slate-600 truncate">{u.name}</div>
            <div className="col-span-2">
              <span className="px-2 py-0.5 rounded text-xs uppercase tracking-wider"
                style={{
                  background: u.role === "admin" ? "#3B82F619" : "#FFFFFF0D",
                  color: u.role === "admin" ? "#3B82F6" : "#FFFFFF",
                  border: u.role === "admin" ? "1px solid #3B82F633" : "1px solid #FFFFFF1A",
                }}>{u.role}</span>
            </div>
            <div className="col-span-2 text-slate-500 text-xs">{u.auth_provider}</div>
            <div className="col-span-2 flex justify-end gap-2">
              {isMe ? (
                <span className="text-xs text-slate-400">you</span>
              ) : u.role === "admin" ? (
                <Button size="sm" variant="ghost" className="text-xs border border-slate-200 hover:bg-slate-100" onClick={() => setRole(u, "merchant")} data-testid={`user-demote-${u.user_id}`}>Demote</Button>
              ) : (
                <Button size="sm" variant="ghost" className="text-xs border border-[#3B82F6]/40 text-[#3B82F6] hover:bg-[#3B82F6]/10" onClick={() => setRole(u, "admin")} data-testid={`user-promote-${u.user_id}`}>Promote</Button>
              )}
              {!isMe && (
                <Button size="sm" variant="ghost" className={`text-xs border ${u.deactivated ? "border-[#10B981]/40 text-[#10B981] hover:bg-[#10B981]/10" : "border-[#FF4757]/40 text-[#FF4757] hover:bg-[#FF4757]/10"}`}
                  onClick={() => toggleActive(u)} data-testid={`user-toggle-${u.user_id}`}>
                  {u.deactivated ? "Reactivate" : "Deactivate"}
                </Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
